import { z } from "zod";
export * from "./search.js";
export * from "./flows.js";
export * from "./processes.js";
export * from "./service-ticket.js";
export * from "./appointments.js";
export * from "./stock.js";
export * from "./access.js";
export * from "./contact-priority.js";
export * from "./login-composition.js";
export const roleSchema = z.enum(["DIRECTOR", "ADMIN", "SUPERVISOR", "AGENT"]);
export const userEmploymentStatusSchema = z.enum([
    "ACTIVE",
    "VACATION",
    "LEAVE",
    "DISABLED"
]);
export const ticketStatusSchema = z.enum([
    "WAITING",
    "OPEN",
    "PENDING",
    "RESOLVED"
]);
export const analyticsRangeSchema = z
    .enum(["24h", "7d", "30d", "12w", "12m", "custom"])
    .default("30d");
const analyticsIdListSchema = z.preprocess((value) => {
    if (Array.isArray(value))
        return value;
    if (typeof value !== "string" || !value.trim())
        return [];
    return value.split(",").filter(Boolean);
}, z
    .array(z.string().trim().min(1).max(120))
    .max(50)
    .default([])
    .transform((values) => [...new Set(values)]));
const analyticsDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Use uma data no formato AAAA-MM-DD")
    .refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, "Informe uma data válida");
export const analyticsDashboardQuerySchema = z
    .object({
    range: analyticsRangeSchema,
    from: analyticsDateSchema.optional(),
    to: analyticsDateSchema.optional(),
    queueIds: analyticsIdListSchema,
    technicianIds: analyticsIdListSchema,
    companyIds: analyticsIdListSchema,
    tagIds: analyticsIdListSchema
})
    .superRefine((value, context) => {
    if (value.range !== "custom")
        return;
    if (!value.from) {
        context.addIssue({
            code: "custom",
            path: ["from"],
            message: "Informe a data inicial do período personalizado"
        });
    }
    if (!value.to) {
        context.addIssue({
            code: "custom",
            path: ["to"],
            message: "Informe a data final do período personalizado"
        });
    }
    if (value.from && value.to && value.from > value.to) {
        context.addIssue({
            code: "custom",
            path: ["to"],
            message: "A data final deve ser igual ou posterior à inicial"
        });
    }
});
export const analyticsSectionSchema = z.enum([
    "summary",
    "volume",
    "operation",
    "messages",
    "queues",
    "technicians",
    "tags",
    "quality"
]);
export const defaultAnalyticsSections = [
    "summary",
    "volume",
    "operation",
    "messages",
    "queues",
    "technicians",
    "tags",
    "quality"
];
export const analyticsViewConfigurationSchema = z.object({
    filters: analyticsDashboardQuerySchema,
    sections: z
        .array(analyticsSectionSchema)
        .min(1, "Escolha pelo menos uma seção")
        .max(defaultAnalyticsSections.length)
        .default(defaultAnalyticsSections)
});
export const createAnalyticsViewSchema = z.object({
    name: z.string().trim().min(2).max(80),
    isDefault: z.boolean().default(false),
    configuration: analyticsViewConfigurationSchema
});
export const updateAnalyticsViewSchema = createAnalyticsViewSchema
    .partial()
    .extend({ expectedVersion: z.number().int().positive() })
    .refine(({ expectedVersion: _expectedVersion, ...changes }) => Object.values(changes).some((item) => item !== undefined), { message: "Informe ao menos uma alteração" });
export const deleteAnalyticsViewSchema = z.object({
    expectedVersion: z.number().int().positive()
});
export const messageTypeSchema = z.enum([
    "TEXT",
    "IMAGE",
    "DOCUMENT",
    "AUDIO",
    "VIDEO",
    "STICKER",
    "LOCATION",
    "CONTACT",
    "REACTION",
    "INTERACTIVE",
    "TEMPLATE",
    "UNSUPPORTED"
]);
export const messageStatusSchema = z.enum([
    "RECEIVED",
    "QUEUED",
    "SENT",
    "DELIVERED",
    "READ",
    "FAILED"
]);
const normalizedEmailSchema = z
    .string()
    .trim()
    .pipe(z.email())
    .transform((value) => value.toLowerCase());
export const loginSchema = z.object({
    email: normalizedEmailSchema,
    password: z.string().min(1).max(256)
});
export const loginAvatarMatchSchema = z.object({
    email: normalizedEmailSchema
});
export const strongPasswordSchema = z
    .string()
    .min(12, "A senha deve ter pelo menos 12 caracteres")
    .max(256, "A senha deve ter no máximo 256 caracteres");
export const createUserSchema = z.object({
    name: z.string().trim().min(2).max(120),
    email: normalizedEmailSchema,
    password: strongPasswordSchema,
    role: z.enum(["ADMIN", "AGENT"]).default("AGENT"),
    queueIds: z.array(z.string().min(1)).default([])
});
const optionalAdminDateSchema = z
    .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.null()])
    .optional();
export const updateAdminUserSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    email: normalizedEmailSchema.optional(),
    role: z.enum(["DIRECTOR", "ADMIN", "AGENT"]).optional(),
    employmentStatus: userEmploymentStatusSchema.optional(),
    loginEnabled: z.boolean().optional(),
    participatesInProductivity: z.boolean().optional(),
    statusStartDate: optionalAdminDateSchema,
    statusEndDate: optionalAdminDateSchema,
    absenceReason: z.union([z.string().trim().max(500), z.null()]).optional(),
    admissionDate: optionalAdminDateSchema,
    deactivationReason: z
        .union([z.string().trim().max(500), z.null()])
        .optional(),
    queueIds: z.array(z.string().min(1)).optional(),
    forcePasswordChange: z.boolean().optional(),
    confirmSelf: z.boolean().optional()
});
export const permanentlyDeleteUserSchema = z.object({
    confirmation: z.string().trim().min(1).max(320),
    reason: z.string().trim().min(5).max(500)
});
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1).max(256),
    newPassword: strongPasswordSchema
})
    .refine((value) => value.currentPassword !== value.newPassword, {
    path: ["newPassword"],
    message: "A nova senha deve ser diferente da senha atual"
});
export const updateUserPreferencesSchema = z.object({
    messageSignatureEnabled: z.boolean()
});
export const e164Schema = z
    .string()
    .trim()
    .transform((value) => (value.startsWith("+") ? value : `+${value}`))
    .refine((value) => /^\+[1-9]\d{7,14}$/.test(value), {
    message: "Informe o telefone com DDI, por exemplo +5535999999999"
});
export const createContactSchema = z.object({
    name: z.string().trim().min(2).max(160),
    phone: e164Schema,
    email: z.email().optional().or(z.literal("")),
    jobTitle: z.string().trim().max(160).optional().default(""),
    notes: z.string().max(5000).optional().default(""),
    companyId: z.preprocess((value) => value === "" ? null : value, z.string().min(1).nullable().optional()),
    departmentId: z.preprocess((value) => value === "" ? null : value, z.string().min(1).nullable().optional()),
    tagIds: z.array(z.string().min(1)).default([])
});
export const updateContactSchema = z
    .object({
    name: z.string().trim().min(2).max(160).optional(),
    phone: e164Schema.optional(),
    email: z.email().optional().or(z.literal("")),
    jobTitle: z.string().trim().max(160).optional(),
    notes: z.string().max(5000).optional(),
    companyId: z.string().min(1).nullable().optional(),
    departmentId: z.string().min(1).nullable().optional(),
    tagIds: z.array(z.string().min(1)).optional(),
    ninjaDeviceId: z.number().int().positive().nullable().optional(),
    expectedUpdatedAt: z.string().datetime().optional()
})
    .refine(({ expectedUpdatedAt: _expectedUpdatedAt, ...changes }) => Object.values(changes).some((item) => item !== undefined), { message: "Informe ao menos uma alteração" });
export const contactSortSchema = z.enum(["name", "recent"]).default("name");
export const defaultCompanyDepartments = [
    "Administrativo",
    "Financeiro",
    "Comercial",
    "RH",
    "Diretoria",
    "TI",
    "Suporte",
    "Operacional",
    "Produção",
    "Logística",
    "Compras",
    "Marketing",
    "Jurídico",
    "Contabilidade",
    "Outros"
];
export const createCompanySchema = z.object({
    name: z.string().trim().min(2).max(160),
    legalName: z.string().trim().max(200).optional().default(""),
    taxId: z.string().trim().max(24).optional().default(""),
    phone: z.string().trim().max(32).optional().default(""),
    email: z.email().optional().or(z.literal("")).default(""),
    address: z.string().trim().max(500).optional().default(""),
    website: z.string().trim().max(300).optional().default(""),
    notes: z.string().max(5000).optional().default(""),
    externalId: z.string().trim().max(160).optional().or(z.literal("")),
    isActive: z.boolean().optional().default(true)
});
export const updateCompanySchema = createCompanySchema
    .partial()
    .extend({ expectedUpdatedAt: z.string().datetime().optional() })
    .refine(({ expectedUpdatedAt: _expectedUpdatedAt, ...changes }) => Object.values(changes).some((item) => item !== undefined), { message: "Informe ao menos uma alteração" });
export const createCompanyDepartmentSchema = z.object({
    name: z.string().trim().min(2).max(100)
});
export const updateCompanyDepartmentSchema = createCompanyDepartmentSchema;
export const recordConsentSchema = z.object({
    status: z.enum(["GRANTED", "REVOKED"]),
    source: z.string().trim().min(2).max(160),
    purpose: z.string().trim().min(2).max(500),
    evidence: z.string().trim().max(1000).optional().default("")
});
const ticketVersionSchema = z.number().int().nonnegative();
export const claimTicketSchema = z.object({
    expectedVersion: ticketVersionSchema
});
export const reopenTicketSchema = z.object({
    expectedVersion: ticketVersionSchema
});
export const responseAlertActionSchema = z.enum([
    "AWAIT_CUSTOMER",
    "NO_REPLY_REQUIRED",
    "SNOOZE_30",
    "SNOOZE_60",
    "SNOOZE_1440",
    "RESUME"
]);
export const updateTicketSchema = z
    .object({
    status: ticketStatusSchema.optional(),
    assignedUserId: z.string().nullable().optional(),
    queueId: z.string().optional(),
    responseAlertAction: responseAlertActionSchema.optional(),
    surveyOverride: z.literal("SKIP").optional(),
    surveyOverrideReason: z.string().trim().max(1000).optional(),
    expectedVersion: ticketVersionSchema
})
    .refine((value) => value.status !== undefined ||
    value.assignedUserId !== undefined ||
    value.queueId !== undefined ||
    value.responseAlertAction !== undefined ||
    value.surveyOverride !== undefined, {
    message: "Informe ao menos uma alteração"
});
export const conversationAuditSessionSchema = z.object({
    sessionId: z.string().uuid()
});
export const conversationMessageViewSchema = conversationAuditSessionSchema.extend({
    messageIds: z.array(z.string().trim().min(1).max(128)).min(1).max(100)
});
export const sendMessageSchema = z.object({
    text: z.string().trim().min(1).max(4096),
    replyToMessageId: z.string().optional(),
    idempotencyKey: z.string().uuid()
});
export const sendQuickReplySchema = z.object({
    quickReplyId: z.string().trim().min(1),
    replyToMessageId: z.string().optional(),
    idempotencyKey: z.string().uuid()
});
export const globalSearchQuerySchema = z
    .string()
    .trim()
    .min(2, "Digite ao menos 2 caracteres para pesquisar.")
    .max(120, "A pesquisa deve ter no maximo 120 caracteres.");
export const searchMatchModeSchema = z
    .enum(["exact", "tolerant"])
    .default("tolerant");
export const sendTemplateSchema = z.object({
    templateId: z.string().min(1),
    variables: z.record(z.string(), z.string()).default({})
});
const scheduledForSchema = z.string().datetime({ offset: true });
export const createScheduledMessageSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("TEXT"),
        text: z.string().trim().min(1).max(4096),
        scheduledFor: scheduledForSchema,
        idempotencyKey: z.string().uuid()
    }),
    z.object({
        kind: z.literal("TEMPLATE"),
        templateId: z.string().min(1),
        variables: z.record(z.string(), z.string()).default({}),
        scheduledFor: scheduledForSchema,
        idempotencyKey: z.string().uuid()
    })
]);
export const cancelScheduledMessageSchema = z.object({
    expectedVersion: z.number().int().nonnegative()
});
export const SCHEDULED_MESSAGE_QUEUE_NAME = "scheduled-messages";
const templateVariableSchema = z.object({
    index: z.number().int().positive(),
    label: z.string().trim().min(2).max(80),
    example: z.string().trim().min(1).max(160)
});
export const createMessageTemplateSchema = z
    .object({
    name: z
        .string()
        .trim()
        .min(3)
        .max(512)
        .regex(/^[a-z0-9_]+$/, "Use apenas letras minúsculas, números e sublinhado"),
    language: z.string().regex(/^[a-z]{2}_[A-Z]{2}$/).default("pt_BR"),
    category: z.enum(["UTILITY", "MARKETING"]).default("UTILITY"),
    body: z.string().trim().min(10).max(1024),
    footer: z.string().trim().max(60).optional().default(""),
    variables: z.array(templateVariableSchema).max(20).default([]),
    quickReplies: z.array(z.string().trim().min(1).max(25)).max(3).default([])
})
    .superRefine((value, context) => {
    const bodyIndexes = [
        ...new Set(Array.from(value.body.matchAll(/{{(\d+)}}/g)).map((match) => Number(match[1])))
    ].sort((left, right) => left - right);
    const expected = bodyIndexes.map((_, index) => index + 1);
    if (bodyIndexes.length !== expected.length ||
        bodyIndexes.some((index, position) => index !== expected[position])) {
        context.addIssue({
            code: "custom",
            path: ["body"],
            message: "As variáveis devem ser sequenciais: {{1}}, {{2}}, {{3}}..."
        });
    }
    const supplied = value.variables
        .map((variable) => variable.index)
        .sort((left, right) => left - right);
    if (supplied.length !== bodyIndexes.length ||
        supplied.some((index, position) => index !== bodyIndexes[position])) {
        context.addIssue({
            code: "custom",
            path: ["variables"],
            message: "Informe rótulo e exemplo para todas as variáveis do texto"
        });
    }
});
export const createNoteSchema = z.object({
    body: z.string().trim().min(1).max(5000)
});
export const createInternalNoteMediaSchema = z.object({
    caption: z.string().trim().max(1024).optional().default("")
});
export const createTeamChatMessageSchema = z.object({
    recipientId: z.string().trim().min(1).max(128),
    body: z.string().trim().min(1).max(2000)
});
export const createTeamChatMediaSchema = z.object({
    recipientId: z.string().trim().min(1).max(128),
    caption: z.string().trim().max(2000).optional().default("")
});
export const teamChatParticipantIdSchema = z.string().trim().min(1).max(128);
export const teamChatTextAssistantSchema = z.object({
    participantId: teamChatParticipantIdSchema,
    text: z.string().trim().min(1).max(2000)
});
export const updateTeamChatMessageSchema = z.object({
    body: z.string().trim().min(1).max(2000),
    expectedUpdatedAt: z.string().datetime()
});
export const deleteTeamChatMessageSchema = z.object({
    expectedUpdatedAt: z.string().datetime()
});
export const createTagSchema = z.object({
    name: z.string().trim().min(1).max(60),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/)
});
export const createQueueSchema = z.object({
    name: z.string().trim().min(2).max(80),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    isDefault: z.boolean().default(false)
});
export const updateQueueSchema = z
    .object({
    name: z.string().trim().min(2).max(80).optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    isDefault: z.boolean().optional()
})
    .refine((value) => Object.values(value).some((item) => item !== undefined), {
    message: "Informe ao menos uma alteração"
});
export const serviceAutomationDaySchema = z.object({
    id: z.enum([
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
    ]),
    label: z.string().trim().min(1).max(12),
    enabled: z.boolean(),
    start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)
});
export const serviceAutomationSettingsSchema = z.object({
    greetingEnabled: z.boolean(),
    greetingTrigger: z.enum(["QUEUE_CHANGE", "QUEUE_ENTRY"]),
    greetingMessage: z.string().trim().min(1).max(4096),
    officeHoursEnabled: z.boolean(),
    timezone: z.enum([
        "America/Sao_Paulo",
        "America/Manaus",
        "America/Fortaleza"
    ]),
    schedule: z
        .array(serviceAutomationDaySchema)
        .length(7)
        .refine((days) => new Set(days.map((day) => day.id)).size === 7, {
        message: "Configure cada dia da semana uma única vez"
    })
        .refine((days) => days.every((day) => !day.enabled || day.start < day.end), { message: "O início do expediente deve ser anterior ao fim" }),
    outsideHoursMessage: z.string().trim().min(1).max(4096),
    routingMode: z.enum(["ROUND_ROBIN", "BALANCED", "MANUAL"]),
    respectAvailability: z.boolean(),
    maxOpenTickets: z.number().int().min(1).max(50),
    waitingAlertEnabled: z.boolean(),
    waitingMinutes: z.number().int().min(1).max(240),
    markPriority: z.boolean(),
    markContactAlert: z.boolean(),
    destinationQueueId: z.string().trim().max(120),
    assignUserId: z.string().trim().max(120),
    notifyTeam: z.boolean()
});
export const defaultServiceAutomationSettings = {
    greetingEnabled: false,
    greetingTrigger: "QUEUE_CHANGE",
    greetingMessage: "Olá, {{nome}}! Seu atendimento foi direcionado para {{fila}}. Um técnico continuará com você por aqui.",
    officeHoursEnabled: false,
    timezone: "America/Sao_Paulo",
    schedule: [
        { id: "monday", label: "Seg", enabled: true, start: "08:00", end: "18:00" },
        { id: "tuesday", label: "Ter", enabled: true, start: "08:00", end: "18:00" },
        { id: "wednesday", label: "Qua", enabled: true, start: "08:00", end: "18:00" },
        { id: "thursday", label: "Qui", enabled: true, start: "08:00", end: "18:00" },
        { id: "friday", label: "Sex", enabled: true, start: "08:00", end: "18:00" },
        { id: "saturday", label: "Sáb", enabled: false, start: "08:00", end: "12:00" },
        { id: "sunday", label: "Dom", enabled: false, start: "08:00", end: "12:00" }
    ],
    outsideHoursMessage: "Recebemos sua mensagem fora do nosso horário de atendimento. Retornaremos no próximo período útil.",
    routingMode: "MANUAL",
    respectAvailability: true,
    maxOpenTickets: 8,
    waitingAlertEnabled: false,
    waitingMinutes: 20,
    markPriority: true,
    markContactAlert: true,
    destinationQueueId: "",
    assignUserId: "",
    notifyTeam: true
};
const serviceDayByShortName = {
    Mon: "monday",
    Tue: "tuesday",
    Wed: "wednesday",
    Thu: "thursday",
    Fri: "friday",
    Sat: "saturday",
    Sun: "sunday"
};
export function isWithinServiceHours(settings, date = new Date()) {
    if (!settings.officeHoursEnabled)
        return true;
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: settings.timezone,
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23"
    }).formatToParts(date);
    const value = (type) => parts.find((part) => part.type === type)?.value || "";
    const day = settings.schedule.find((item) => item.id === serviceDayByShortName[value("weekday")]);
    if (!day?.enabled)
        return false;
    const time = `${value("hour")}:${value("minute")}`;
    return time >= day.start && time < day.end;
}
export const quickReplyButtonSchema = z.object({
    id: z
        .string()
        .trim()
        .min(1)
        .max(256)
        .regex(/^[a-zA-Z0-9_-]+$/, "Use apenas letras, números, hífen e sublinhado no ID"),
    title: z.string().trim().min(1).max(20)
});
export const quickReplyButtonsSchema = z.preprocess((value) => {
    if (typeof value !== "string")
        return value;
    try {
        return JSON.parse(value);
    }
    catch {
        return value;
    }
}, z.array(quickReplyButtonSchema).max(3).default([]));
export const createQuickReplySchema = z
    .object({
    shortcut: z
        .string()
        .trim()
        .min(2)
        .max(40)
        .regex(/^\/[a-zA-Z0-9_-]+$/, "O atalho deve começar com /"),
    title: z.string().trim().min(2).max(100),
    content: z.string().trim().min(1).max(4096),
    kind: z.enum(["TEXT", "TUTORIAL", "BUTTONS"]).default("TEXT"),
    category: z
        .enum(["GENERAL", "SUPPORT", "ACCESS", "BILLING", "ONBOARDING"])
        .default("GENERAL"),
    buttons: quickReplyButtonsSchema
})
    .superRefine((reply, context) => {
    if (reply.kind === "BUTTONS" && !reply.buttons.length) {
        context.addIssue({ code: "custom", path: ["buttons"], message: "Adicione ao menos um botão" });
    }
    if (reply.kind !== "BUTTONS" && reply.buttons.length) {
        context.addIssue({ code: "custom", path: ["buttons"], message: "Botões só podem ser usados em mensagens interativas" });
    }
    if (reply.buttons.length && reply.content.length > 1024) {
        context.addIssue({ code: "custom", path: ["content"], message: "Mensagens com botões podem ter no máximo 1024 caracteres" });
    }
    if (new Set(reply.buttons.map((button) => button.id)).size !== reply.buttons.length) {
        context.addIssue({ code: "custom", path: ["buttons"], message: "Os IDs dos botões não podem se repetir" });
    }
});
export const SERVICE_WINDOW_MS = 24 * 60 * 60 * 1000;
export function isActiveTicketStatus(status) {
    return status !== "RESOLVED";
}
export function inboundTicketStatus(currentStatus, assignedUserId) {
    if (currentStatus === "WAITING")
        return "WAITING";
    if (currentStatus === "PENDING") {
        return assignedUserId ? "OPEN" : "WAITING";
    }
    if (currentStatus === "OPEN" && !assignedUserId)
        return "WAITING";
    return currentStatus;
}
export function serviceWindowExpiresAt(lastCustomerInteractionAt) {
    return new Date(lastCustomerInteractionAt.getTime() + SERVICE_WINDOW_MS);
}
export function isServiceWindowOpen(expiresAt, now = new Date()) {
    if (!expiresAt)
        return false;
    return new Date(expiresAt).getTime() > now.getTime();
}
export function canReopenResolvedTicket(status, expiresAt, now = new Date()) {
    return status === "RESOLVED" && isServiceWindowOpen(expiresAt, now);
}
const messageStatusOrder = {
    QUEUED: 0,
    SENT: 1,
    DELIVERED: 2,
    READ: 3,
    FAILED: 4
};
export function canAdvanceMessageStatus(current, next) {
    if (next === "FAILED")
        return current !== "READ";
    return ((messageStatusOrder[next] ?? -1) >=
        (messageStatusOrder[current] ?? -1));
}
export * from "./appointment-recurrence.js";
export * from "./surveys.js";
